import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild, OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-slider',
  imports: [CommonModule],
  templateUrl: './slider.component.html',
  styleUrl: './slider.component.scss',
})
export class SliderComponent implements OnChanges, OnInit {
  @Input() numberOfTicks = 5;
  @Input() text = '';
  @Input() icon = '';
  @Input() active = true;
  @Input() finished = false;
  @Input() index = 0;
  @Input() showOutline = false;

  @Output() sliderChange = new EventEmitter<number>();
  @Output() tickIndexChange = new EventEmitter<number>();

  @ViewChild('sliderThumb') sliderThumb!: ElementRef;
  @ViewChild('sliderTrack') sliderTrack!: ElementRef;
  @ViewChild('sliderContainer') sliderContainer!: ElementRef;
  @ViewChild('sliderTrackProgress') sliderTrackProgress!: ElementRef;
  @ViewChild('mood', { static: true }) mood!: TemplateRef<{ color: string }>;
  @ViewChild('relaxation', { static: true }) relaxation!: TemplateRef<{ color: string }>;
  @ViewChild('energy', { static: true }) energy!: TemplateRef<{ color: string }>;
  @ViewChild('sleep', { static: true }) sleep!: TemplateRef<{ color: string }>;
  @ViewChild('motivation', { static: true }) motivation!: TemplateRef<{ color: string }>;

  templateMap: Record<string, TemplateRef<{ color: string }>> = {};

  showThumb = true;
  tickIndex = -1;
  showLoader = true;

  get showPulsingAnimation(): boolean {
    return this.index === 0 && this.showLoader;
  }

  get fontAwesomeIcon(): boolean {
    return this.icon.includes('fa-');
  }

  get iconTemplate(): TemplateRef<{ color: string }> | null {
    return this.templateMap[this.icon] || null;
  }

  private isDragging = false;
  private initialClickOffset = 0;

  constructor(private renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['finished']) {
      this.finished = changes['finished']?.currentValue;
    }

    if (changes['active']) {
      this.active = changes['active']?.currentValue;

      if (this.active) {
        this.resetSliderToActive();
      } else {
        this.setProgressBarHeight();
      }
    }
  }

  ngOnInit(): void {
    this.templateMap = {
      mood: this.mood,
      relaxation: this.relaxation,
      energy: this.energy,
      sleep: this.sleep,
      motivation: this.motivation,
    };
  }

  turnBottomBarBlue(): void {
    if (!this.sliderTrack?.nativeElement || !this.sliderThumb?.nativeElement || !this.sliderTrackProgress?.nativeElement) {
      return;
    }

    const sliderRadius = 16;
    const trackRect = this.sliderTrack.nativeElement.getBoundingClientRect();
    let newTop = 0;

    if (event instanceof MouseEvent) {
      newTop = event.clientY - trackRect.top - sliderRadius - this.initialClickOffset;
    } else if (event instanceof TouchEvent) {
      newTop = event.touches[0].clientY - trackRect.top - sliderRadius - this.initialClickOffset;
    }

    const startPoint = this.sliderThumb.nativeElement.offsetHeight / 2;
    const maxTop = trackRect.height - startPoint;
    const minTop = -startPoint;

    newTop = Math.max(minTop, Math.min(newTop, maxTop));

    const progressHeight = trackRect.height - (newTop + startPoint);
    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'height', `${progressHeight}px`);
    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'transition', 'background-color 1s ease, opacity 1s ease');
    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'background-color', '#2cdaff');
    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'opacity', '1');
  }

  startDrag(event: MouseEvent | TouchEvent): void {
    if (!this.active || !this.sliderThumb?.nativeElement) {
      return;
    }

    this.isDragging = true;
    this.showLoader = false;

    const thumbRect = this.sliderThumb.nativeElement.getBoundingClientRect();
    const thumbCenter = thumbRect.top + thumbRect.height / 2;

    let clickY = 0;
    if (event instanceof MouseEvent) {
      clickY = event.clientY;
    } else if (event instanceof TouchEvent) {
      clickY = event.touches[0].clientY;
    }

    this.initialClickOffset = clickY - thumbCenter;

    if (event instanceof MouseEvent) {
      this.renderer.listen('document', 'mousemove', this.onDrag);
      this.renderer.listen('document', 'mouseup', () => { this.stopDrag(); });
    } else if (event instanceof TouchEvent) {
      this.renderer.listen('document', 'touchmove', this.onDrag);
      this.renderer.listen('document', 'touchend', () => { this.stopDrag(); });
    }
  }

  onDrag = (event: MouseEvent | TouchEvent): void => {
    if (!this.isDragging ||
        !this.sliderTrack?.nativeElement ||
        !this.sliderThumb?.nativeElement ||
        !this.sliderTrackProgress?.nativeElement) {
      return;
    }

    const sliderRadius = 16;
    const trackRect = this.sliderTrack.nativeElement.getBoundingClientRect();
    let newTop = 0;
    this.turnBottomBarBlue();

    if (event instanceof MouseEvent) {
      newTop = event.clientY - trackRect.top - sliderRadius - this.initialClickOffset;
    } else if (event instanceof TouchEvent) {
      newTop = event.touches[0].clientY - trackRect.top - sliderRadius - this.initialClickOffset;
    }

    const startPoint = this.sliderThumb.nativeElement.offsetHeight / 2;
    const maxTop = trackRect.height - startPoint;
    const minTop = -startPoint;
    const thumbRadius = 23;

    newTop = Math.max(minTop, Math.min(newTop, maxTop));

    this.renderer.setStyle(this.sliderThumb.nativeElement, 'top', `${newTop + thumbRadius}px`);
    const progressHeight = trackRect.height - (newTop + startPoint);
    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'height', `${progressHeight}px`);
    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'transition', '');
    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'opacity', '1');

    const numberOfTicks = Math.max(2, this.numberOfTicks - 1);
    const tickHeight = trackRect.height / numberOfTicks;

    this.tickIndex = Math.round((newTop + thumbRadius) / tickHeight);
    this.tickIndexChange.emit(this.tickIndex);
  };

  stopDrag = async (): Promise<void> => {
    if (!this.isDragging ||
        !this.sliderTrack?.nativeElement ||
        !this.sliderThumb?.nativeElement ||
        !this.sliderTrackProgress?.nativeElement) {
      return;
    }

    if (this.tickIndex === -1) {
      this.isDragging = false;
      this.resetSliderToActive();

      return;
    }

    const trackRect = this.sliderTrack.nativeElement.getBoundingClientRect();
    const numberOfTicks = Math.max(2, this.numberOfTicks - 1);
    const tickHeight = trackRect.height / numberOfTicks;

    this.isDragging = false;
    const tick = this.tickIndex * tickHeight;

    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'height', `calc(100% - ${tick}px)`);
    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'transition', 'height 0.5s ease');
    this.renderer.setStyle(this.sliderThumb.nativeElement, 'top', `calc(${tick}px)`);
    this.renderer.setStyle(this.sliderThumb.nativeElement, 'transition', 'top 0.5s ease');
    this.renderer.setStyle(this.sliderTrack.nativeElement, 'transition', 'background-color 0.5s ease, height 0.5s ease');

    setTimeout(() => {
      this.showThumb = false;
      this.renderer.setStyle(this.sliderTrack.nativeElement, 'background-color', `transparent`);
      this.renderer.setStyle(this.sliderTrack.nativeElement, 'box-shadow', `none`);
      this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'height', `calc(100% - ${tick}px)`);
      this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'min-height', `15px`);
      this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'opacity', '1');

      if (this.showThumb) {
        this.renderer.setStyle(this.sliderThumb.nativeElement, 'opacity', '0');
      }
    }, 700);

    await new Promise(resolve => setTimeout(resolve, 500));

    setTimeout(() => {
      this.finished = true;

      const sliderValue = Math.abs(this.tickIndex - 5);
      this.sliderChange.emit(sliderValue);
      this.tickIndexChange.emit(-1);
    }, 500);
  };

  setToTickIndex(tickIndex: number): void {
    this.tickIndex = tickIndex;
    const trackRect = this.sliderTrack.nativeElement.getBoundingClientRect();
    const numberOfTicks = Math.max(2, this.numberOfTicks - 1);
    const tickHeight = trackRect.height / numberOfTicks;
    const tick = tickIndex * tickHeight;

    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'height', `calc(100% - ${tick}px)`);
    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'opacity', '1');
    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'transition', 'height 0.5s ease');
    this.renderer.setStyle(this.sliderTrack.nativeElement, 'background-color', `transparent`);
    this.renderer.setStyle(this.sliderTrack.nativeElement, 'box-shadow', `none`);
  }

  private resetSliderToActive(): void {
    if (!this.sliderTrack) {
      return;
    }

    this.showThumb = true;
    this.active = true;
    this.finished = false;
    this.renderer.setStyle(this.sliderTrack.nativeElement, 'height', '100%');
    this.renderer.removeStyle(this.sliderTrack.nativeElement, 'background-color');
    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'opacity', '0');
  }

  private setProgressBarHeight(): void {
    if (!this.sliderTrack || !this.sliderTrackProgress) {
      return;
    }

    if (this.tickIndex > 0) {
      this.finished = true;
      this.renderer.setStyle(this.sliderTrack.nativeElement, 'background-color', 'transparent');
    }

    this.renderer.setStyle(this.sliderTrackProgress.nativeElement, 'opacity', '1');
    this.showThumb = false;
    this.active = false;
  }
}
